"use client";

import { type FC, type PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";
import { P, match } from "ts-pattern";
import {
	type ApiData,
	canSplitHorizontally,
	canSplitVertically,
	createHorizontals,
	createVerticals,
	defaultState,
	deserialize,
	getMaterialThickness,
	getShouldHaveBlindVertical,
	isLongPartsBuildMethod,
	serialize,
} from "../components/renderer/data";
import { BACKGROUND_THICKNESS, createBackground } from "../components/renderer/data/Background";
import {
	type CompartmentType,
	type HorizontalSide,
	type Side,
	type VerticalSide,
	canMergeWith,
	getAllSnappingPointsOnXAxis,
	getAllSnappingPointsOnYAxis,
	getBottomCompartmentsRecursively,
	getCompartmentsOnSide,
	getLeftCompartmentsRecursively,
	getMaxSize,
	getMinSize,
	getRightCompartmentsRecursively,
	getSurroundingCompartments,
	getTopCompartmentsRecursively,
	updateCompartment,
} from "../components/renderer/data/Compartment";
import { type DoorSide, createDoor } from "../components/renderer/data/Door";
import { createDoubleDoor } from "../components/renderer/data/DoubleDoor";
import { createShelves } from "../components/renderer/data/Shelf";
import { FEET_HEIGHT } from "../components/renderer/parts/Horizontal";
import { IdPrefix, adjustDimension, generateId } from "../components/renderer/utils";
import type { ConfigContextValue } from "./ConfigContext";
import { type CabinetState, CabinetStoreContext } from "./StoreContext";

interface Props extends Pick<ConfigContextValue, "material" | "thickness"> {
	orderData?: string;
	api: ConfigContextValue["data"];
}

export const CabinetStoreProvider: FC<PropsWithChildren<Props>> = (props) => {
	const { children, orderData, api: defaultApi, material, thickness } = props;
	const [api, setApi] = useState<ApiData | undefined>(defaultApi);

	const deserializedOrdeData = useMemo(() => {
		return orderData ? deserialize(orderData) : undefined;
	}, [orderData]);

	const defaultStore = useMemo(() => {
		return defaultState(2500, 2000, 400, 38, "10033-4082-38", api);
	}, [api]);

	const [store, setStore] = useState<CabinetState>(deserializedOrdeData ?? defaultStore);

	// biome-ignore lint/correctness/useExhaustiveDependencies: This effect should only run when the material or thickness changes
	useEffect(() => {
		if (material || thickness) {
			match({ material, thickness })
				.with({ material: P.string, thickness: P.number }, (v) => {
					setMaterial(v.material);
					setThickness(store, v.thickness, v.material);
				})
				.with({ material: P.string, thickness: P.nullish }, (v) => setMaterial(v.material))
				.with({ material: P.nullish, thickness: P.number }, (v) => setThickness(store, v.thickness))
				.run();
		}
	}, [material, thickness]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: This effect should only run when the orderData changes
	useEffect(() => {
		if (deserializedOrdeData && serialize(deserializedOrdeData) !== serialize(store)) {
			setStore(deserializedOrdeData);
		}
	}, [deserializedOrdeData]);

	type StateType = { index: number; state: CabinetState };

	const [index, setIndex] = useState(0);
	const [states, setStates] = useState<StateType[]>([{ index: 0, state: store }]);

	const current = states.find((state) => state.index === index);
	const pastStates = states.filter((state) => state.index < index);
	const futureStates = states.filter((state) => state.index > index);

	// biome-ignore lint/correctness/useExhaustiveDependencies: This effect should only run when the store changes
	useEffect(() => {
		if (current?.state && serialize(current.state) !== serialize(store)) {
			const newIndex = index + 1;
			setIndex(newIndex);
			setStates((innerStates) => [
				...innerStates.filter((innerState) => innerState.index <= index),
				{ index: newIndex, state: store },
			]);
		}
	}, [store]);

	const undo = () => {
		if (pastStates.length) {
			setIndex((i) => i - 1);

			const newState = states.find((state) => state.index === index - 1);
			newState?.state && setStore(newState.state);
		}
	};

	const redo = () => {
		if (futureStates.length) {
			setIndex((i) => i + 1);

			const newState = states.find((state) => state.index === index + 1);
			newState?.state && setStore(newState.state);
		}
	};

	const getOuterConstraints = useCallback(
		(material: string, thickness: number) => {
			if (isLongPartsBuildMethod(thickness)) {
				const current = getMaterialThickness(material, thickness, api);

				return {
					width: {
						min: 300,
						max: current?.parts.horizontal.constraints.width.max
							? Math.floor((current?.parts.horizontal.constraints.width.max - thickness) / 10) * 10
							: 10000,
					},
					height: { min: 300, max: 4000 },
					depth: { min: 240, max: 600 },
				};
			}

			return {
				width: { min: 300, max: 10000 },
				height: { min: 300, max: 4000 },
				depth: { min: 240, max: 600 },
			};
		},
		[api],
	);

	const setThickness = async (state: CabinetState, newThickness: number, newMaterial?: string) => {
		const previousThickness = state.thickness;
		const thickness = getMaterialThickness(newMaterial ?? state.material, newThickness, api);

		const thicknessDiff = newThickness - state.thickness;
		const compartments = new Map(state.compartments);

		for (const compartment of compartments.values()) {
			const {
				left: leftCompartments,
				right: rightCompartments,
				top: topCompartments,
				bottom: bottomCompartments,
			} = getSurroundingCompartments(
				compartment,
				Array.from(state.compartments.values()),
				state.thickness,
			);

			let totalWidth = leftCompartments.length > 0 ? state.thickness / 2 : state.thickness;
			totalWidth += rightCompartments.length > 0 ? state.thickness / 2 : state.thickness;
			totalWidth += compartment.width;

			let totalHeight = bottomCompartments.length > 0 ? state.thickness / 2 : state.thickness;
			totalHeight += topCompartments.length > 0 ? state.thickness / 2 : state.thickness;
			totalHeight += compartment.height;

			const newTotalWidth =
				totalWidth -
				(leftCompartments.length > 0 ? newThickness / 2 : newThickness) -
				(rightCompartments.length > 0 ? newThickness / 2 : newThickness);

			const newTotalHeight =
				totalHeight -
				(topCompartments.length > 0 ? newThickness / 2 : newThickness) -
				(bottomCompartments.length > 0 ? newThickness / 2 : newThickness);

			const xCorrection = leftCompartments.length > 0 ? thicknessDiff / 2 : thicknessDiff;
			const yCorrection = bottomCompartments.length > 0 ? thicknessDiff / 2 : thicknessDiff;

			compartments.set(
				compartment.id,
				updateCompartment(
					{
						...compartment,
						x: compartment.x + xCorrection,
						y: compartment.y + yCorrection,
						width: newTotalWidth,
						height: newTotalHeight,
					},
					newThickness,
				),
			);
		}

		// Update state
		state.compartments = compartments;
		state.horizontals = createHorizontals(compartments, newThickness, state.material, api);
		state.verticals = createVerticals(compartments, newThickness, state.width);
		state.thickness = newThickness;
		state.material = thickness?.sku ?? state.material;

		// Get outer dimensions
		const constraints = getOuterConstraints(state.material, newThickness);

		const newHeight = isLongPartsBuildMethod(previousThickness)
			? state.height + FEET_HEIGHT * 1000
			: isLongPartsBuildMethod(newThickness)
				? state.height - FEET_HEIGHT * 1000
				: state.height;

		const { adjustedWidth, adjustedHeight, adjustedDepth } = {
			adjustedWidth: adjustDimension(state.width, constraints.width.min, constraints.width.max),
			adjustedHeight: adjustDimension(newHeight, constraints.height.min, constraints.height.max),
			adjustedDepth: adjustDimension(state.depth, constraints.depth.min, constraints.depth.max),
		};

		// Update width
		const widthCompartments = new Map(state.compartments);

		const rightCompartments = Array.from(widthCompartments.values()).filter(
			(compartment) => compartment.x + compartment.width === state.width - state.thickness,
		);
		const widthDiff = adjustedWidth - state.width;

		// Update most right compartments
		for (const compartment of rightCompartments) {
			widthCompartments.set(
				compartment.id,
				updateCompartment(
					{ ...compartment, width: compartment.width + widthDiff },
					state.thickness,
				),
			);
		}

		// Merge and delete compartments
		for (const compartment of widthCompartments.values()) {
			if (compartment.x > adjustedWidth) {
				widthCompartments.delete(compartment.id);
			}
			if (compartment.width < getMinSize(compartment, "left", state, api)) {
				mergeCompartmentsOnResize(widthCompartments, compartment, "left", state);
			}
		}

		// Split compartments that are too big
		splitVerticallyRecursivelyOnResize(widthCompartments, "left", state);

		state.width = adjustedWidth;
		state.compartments = widthCompartments;
		state.horizontals = createHorizontals(widthCompartments, state.thickness, state.material, api);
		state.verticals = createVerticals(widthCompartments, state.thickness, adjustedWidth);

		// Update height
		const heightCompartments = new Map(state.compartments);
		const topCompartments = Array.from(heightCompartments.values()).filter(
			(compartment) => compartment.y + compartment.height === state.height - state.thickness,
		);
		const heightDiff = adjustedHeight - state.height;

		// Update most upper compartments
		for (const compartment of topCompartments) {
			// Future: update updateCompartment() too split and merge everything at once?
			heightCompartments.set(
				compartment.id,
				updateCompartment(
					{ ...compartment, height: compartment.height + heightDiff },
					state.thickness,
				),
			);
		}

		// Merge and delete compartments
		for (const compartment of heightCompartments.values()) {
			if (compartment.y > adjustedHeight) {
				heightCompartments.delete(compartment.id);
			}
			if (compartment.height < getMinSize(compartment, "bottom", state, api)) {
				mergeCompartmentsOnResize(heightCompartments, compartment, "bottom", state);
			}
		}

		// Split compartments that are too big
		splitHorizontallyRecursivelyOnResize(heightCompartments, state);

		state.height = adjustedHeight;
		state.compartments = heightCompartments;
		state.horizontals = createHorizontals(heightCompartments, state.thickness, state.material, api);
		state.verticals = createVerticals(heightCompartments, state.thickness, state.width);

		// Update depth
		const depthCompartments = new Map(state.compartments);

		for (const compartment of depthCompartments.values()) {
			depthCompartments.set(
				compartment.id,
				updateCompartment(
					{
						...compartment,
						depth: adjustedDepth,
						background: compartment.background
							? createBackground({
									...compartment,
									background: {
										...compartment.background,
										z: Math.min(compartment.background.z, adjustedDepth - BACKGROUND_THICKNESS),
									},
								})
							: undefined,
					},
					state.thickness,
				),
			);
		}

		state.depth = adjustedDepth;
		state.compartments = depthCompartments;
		state.horizontals = createHorizontals(depthCompartments, state.thickness, state.material, api);
		state.verticals = createVerticals(depthCompartments, state.thickness, state.width);

		setStore({ ...state });
	};

	const setMaterial = (value: string) => {
		return setStore((state) => ({ ...state, material: value }));
	};

	const splitVertically = (compartment: CompartmentType) => {
		return setStore((state) => {
			const compartments = new Map(state.compartments);
			const newWidth = compartment.width / 2 + state.thickness / 2;

			if (!canSplitVertically(compartment, state, api)) {
				console.warn("Can not split compartment beyond constraints");

				return { ...state };
			}

			const newX = Math.round(
				getAllSnappingPointsOnXAxis(
					compartment,
					compartments,
					state,
					compartment.x + newWidth,
					api,
				),
			);

			const id = generateId(IdPrefix.Compartment);

			compartments.set(
				id,
				updateCompartment(
					{
						id,
						x: newX,
						y: compartment.y,
						z: compartment.z,
						width: compartment.x + compartment.width - newX,
						height: compartment.height,
						depth: compartment.depth,
						background: undefined,
						door: null,
						shelves: [],
					},
					state.thickness,
				),
			);

			compartments.set(
				compartment.id,
				updateCompartment(
					{
						...compartment,
						width: newX - compartment.x - state.thickness,
					},
					state.thickness,
				),
			);

			return {
				...state,
				compartments,
				horizontals: createHorizontals(compartments, state.thickness, state.material, api),
				verticals: createVerticals(compartments, state.thickness, state.width),
			};
		});
	};

	const splitVerticallyRecursivelyOnResize = (
		compartments: Map<string, CompartmentType>,
		side: VerticalSide,
		state: CabinetState,
	) => {
		const queue = Array.from(compartments.values()).filter(
			(c) => c.width > getMaxSize(c, side, state, api),
		);

		while (queue.length > 0) {
			const current = queue.shift();
			if (!current) continue;

			const maxWidth = getMaxSize(current, side, state, api);
			if (current.width <= maxWidth) continue;

			const newWidth = current.width / 2 + state.thickness / 2;
			const newX = Math.round(
				getAllSnappingPointsOnXAxis(current, compartments, state, current.x + newWidth, api),
			);

			const id = generateId(IdPrefix.Compartment);

			const newCompartment = updateCompartment(
				{
					id,
					x: newX,
					y: current.y,
					z: current.z,
					width: current.x + current.width - newX,
					height: current.height,
					depth: current.depth,
					background: undefined,
					door: null,
					shelves: [],
				},
				state.thickness,
			);

			const updatedCompartment = updateCompartment(
				{ ...current, width: newX - current.x - state.thickness },
				state.thickness,
			);

			compartments.set(id, newCompartment);
			compartments.set(current.id, updatedCompartment);

			// Add newly created compartments to the queue if they need further splitting
			if (updatedCompartment.width > maxWidth) queue.push(updatedCompartment);
			if (newCompartment.width > maxWidth) queue.push(newCompartment);
		}

		return compartments;
	};

	const splitHorizontally = (compartment: CompartmentType) => {
		return setStore((state) => {
			const compartments = new Map(state.compartments);
			const newHeight = compartment.height / 2 + state.thickness / 2;

			if (!canSplitHorizontally(compartment, state, api)) {
				console.warn("Can not split compartment beyond constraints");

				return { ...state };
			}

			const newY = Math.round(
				getAllSnappingPointsOnYAxis(
					compartment,
					compartments,
					state,
					compartment.y + newHeight,
					api,
				),
			);

			const id = generateId(IdPrefix.Compartment);

			compartments.set(
				id,
				updateCompartment(
					{
						id,
						y: newY,
						z: compartment.z,
						x: compartment.x,
						height: compartment.y + compartment.height - newY,
						depth: compartment.depth,
						width: compartment.width,
						background: undefined,
						door: null,
						shelves: [],
					},
					state.thickness,
				),
			);

			compartments.set(
				compartment.id,
				updateCompartment(
					{ ...compartment, height: newY - compartment.y - state.thickness },
					state.thickness,
				),
			);

			return {
				...state,
				compartments,
				horizontals: createHorizontals(compartments, state.thickness, state.material, api),
				verticals: createVerticals(compartments, state.thickness, state.width),
			};
		});
	};

	const splitHorizontallyRecursivelyOnResize = (
		compartments: Map<string, CompartmentType>,
		state: CabinetState,
	) => {
		const queue = Array.from(compartments.values()).filter(
			(c) => c.height > getMaxSize(c, "bottom", state, api),
		);

		while (queue.length > 0) {
			const current = queue.shift();
			if (!current) continue;

			if (!canSplitHorizontally(current, state, api)) {
				console.warn("Can not split compartment beyond constraints");
				continue;
			}

			const newHeight = current.height / 2 + state.thickness / 2;
			const newY = Math.round(
				getAllSnappingPointsOnYAxis(current, compartments, state, current.y + newHeight, api),
			);

			const id = generateId(IdPrefix.Compartment);

			const newCompartment = updateCompartment(
				{
					id,
					y: newY,
					z: current.z,
					x: current.x,
					height: current.y + current.height - newY,
					depth: current.depth,
					width: current.width,
					background: undefined,
					door: null,
					shelves: [],
				},
				state.thickness,
			);

			const updatedCompartment = updateCompartment(
				{ ...current, height: newY - current.y - state.thickness },
				state.thickness,
			);

			compartments.set(id, newCompartment);
			compartments.set(current.id, updatedCompartment);

			// Add newly created compartments back to the queue if they need further splitting
			if (updatedCompartment.height > getMaxSize(updatedCompartment, "bottom", state, api)) {
				queue.push(updatedCompartment);
			}
			if (newCompartment.height > getMaxSize(newCompartment, "bottom", state, api)) {
				queue.push(newCompartment);
			}
		}

		return compartments;
	};

	const addShelf = (compartment: CompartmentType) => {
		return setStore((state) => {
			const compartments = new Map(state.compartments);

			compartments.set(
				compartment.id,
				updateCompartment(
					{
						...compartment,
						shelves: createShelves(compartment, compartment.shelves.length + 1, state.thickness),
					},
					state.thickness,
				),
			);

			return { ...state, compartments };
		});
	};

	const removeShelf = (compartment: CompartmentType) => {
		const hasBlindVertical = getShouldHaveBlindVertical(compartment);

		return setStore((state) => {
			const compartments = new Map(state.compartments);

			compartments.set(
				compartment.id,
				updateCompartment(
					{
						...compartment,
						shelves: createShelves(
							compartment,
							hasBlindVertical ? compartment.shelves.length - 2 : compartment.shelves.length - 1,
							state.thickness,
						),
					},
					state.thickness,
				),
			);

			return { ...state, compartments };
		});
	};

	const addDoor = (compartment: CompartmentType, side: DoorSide) => {
		return setStore((state) => {
			const compartments = new Map(state.compartments);

			compartments.set(
				compartment.id,
				updateCompartment(
					{
						...compartment,
						door: createDoor(compartment, side),
					},
					state.thickness,
				),
			);

			return { ...state, compartments };
		});
	};

	const removeDoor = (compartment: CompartmentType) => {
		return setStore((state) => {
			const verticals = state.verticals;
			const blindVertical = Array.from(verticals.values()).find(
				(vertical) =>
					vertical.x > compartment.x &&
					vertical.x < compartment.x + compartment.width &&
					vertical.y === compartment.y,
			);

			if (blindVertical) {
				verticals.delete(blindVertical.id);
			}

			return {
				...state,
				verticals: verticals,
				compartments: new Map(state.compartments).set(
					compartment.id,
					updateCompartment({ ...compartment, door: null }, state.thickness),
				),
			};
		});
	};

	const addDoubleDoor = (compartment: CompartmentType) => {
		return setStore((state) => {
			const compartments = new Map(state.compartments);

			compartments.set(
				compartment.id,
				updateCompartment(
					{
						...compartment,
						door: createDoubleDoor(compartment),
					},
					state.thickness,
				),
			);
			return {
				...state,
				compartments,
				verticals: createVerticals(compartments, state.thickness, state.width),
			};
		});
	};

	const mergeCompartment = (compartment: CompartmentType, side: Side) => {
		return setStore((state) => {
			const compartments = new Map(state.compartments);
			const merge = canMergeWith(compartment, side, state, api);
			if (!merge) return state;

			match(side)
				.with("top", () => {
					compartments.set(
						compartment.id,
						updateCompartment(
							{
								...compartment,
								height: compartment.height + merge.height + state.thickness,
							},
							state.thickness,
						),
					);

					compartments.delete(merge.id);
				})
				.with("bottom", () => {
					compartments.set(
						compartment.id,
						updateCompartment(
							{
								...compartment,
								height: compartment.height + merge.height + state.thickness,
								y: merge.y,
							},
							state.thickness,
						),
					);

					compartments.delete(merge.id);
				})
				.with("left", () => {
					compartments.set(
						compartment.id,
						updateCompartment(
							{
								...compartment,
								width: compartment.width + merge.width + state.thickness,
								x: merge.x,
							},
							state.thickness,
						),
					);

					compartments.delete(merge.id);
				})
				.with("right", () => {
					compartments.set(
						compartment.id,
						updateCompartment(
							{
								...compartment,
								width: compartment.width + merge.width + state.thickness,
							},
							state.thickness,
						),
					);

					compartments.delete(merge.id);
				})
				.exhaustive();

			return {
				...state,
				compartments,
				horizontals: createHorizontals(compartments, state.thickness, state.material, api),
				verticals: createVerticals(compartments, state.thickness, state.width),
			};
		});
	};

	const mergeCompartmentsOnResize = (
		compartments: Map<string, CompartmentType>,
		compartment: CompartmentType,
		side: Side,
		state: CabinetState,
	) => {
		const merge = getCompartmentsOnSide(compartment, side, state);

		match(side)
			.with("bottom", () => {
				for (const mergeCompartment of merge.values()) {
					compartments.set(
						mergeCompartment.id,
						updateCompartment(
							{
								...mergeCompartment,
								height: mergeCompartment.height + compartment.height + state.thickness,
							},
							state.thickness,
						),
					);
				}
				if (compartment.y > state.thickness) {
					compartments.delete(compartment.id);
				}
			})
			.with("top", () => {})
			.with("left", () => {
				for (const mergeCompartment of merge.values()) {
					compartments.set(
						mergeCompartment.id,
						updateCompartment(
							{
								...mergeCompartment,
								width: mergeCompartment.width + compartment.width + state.thickness,
							},
							state.thickness,
						),
					);
				}
				if (compartment.x > state.thickness) {
					compartments.delete(compartment.id);
				}
			})
			.with("right", () => {})
			.exhaustive();

		return compartments;
	};

	const updateCompartmentWidth = (
		compartment: CompartmentType,
		side: VerticalSide,
		diff: number,
	) => {
		return setStore((state) => {
			const compartments = new Map(state.compartments);

			const minSize = getMinSize(compartment, side, state, api);
			const maxSize = getMaxSize(compartment, side, state, api);

			if (side === "left") {
				const newWidth = compartment.width - diff;

				if (newWidth < minSize || newWidth > maxSize) {
					console.warn("Can not resize compartment beyond constraints");

					return { ...state };
				}

				const leftCompartments = getLeftCompartmentsRecursively(
					compartment,
					compartments,
					state.thickness,
				);

				// biome-ignore lint/complexity/noForEach: Type not set correctly when using for loop
				leftCompartments.forEach((left) => {
					const rightCompartments = getRightCompartmentsRecursively(
						left,
						compartments,
						state.thickness,
					);

					// biome-ignore lint/complexity/noForEach: Type not set correctly when using for loop
					rightCompartments.forEach((right) => {
						compartments.set(
							right.id,
							updateCompartment(
								{
									...right,
									x: right.x + diff,
									width: right.width - diff,
								},
								state.thickness,
							),
						);
					});

					compartments.set(
						left.id,
						updateCompartment(
							{
								...left,
								width: left.width + diff,
							},
							state.thickness,
						),
					);
				});
			}

			if (side === "right") {
				const newWidth = compartment.width + diff;

				if (newWidth < minSize || newWidth > maxSize) {
					console.warn("Can not resize compartment beyond constraints");

					return { ...state };
				}

				const rightCompartments = getRightCompartmentsRecursively(
					compartment,
					compartments,
					state.thickness,
				);

				// biome-ignore lint/complexity/noForEach: Type not set correctly when using for loop
				rightCompartments.forEach((right) => {
					const leftCompartments = getLeftCompartmentsRecursively(
						right,
						compartments,
						state.thickness,
					);

					// biome-ignore lint/complexity/noForEach: Type not set correctly when using for loop
					leftCompartments.forEach((left) => {
						compartments.set(
							left.id,
							updateCompartment(
								{
									...left,
									width: left.width + diff,
								},
								state.thickness,
							),
						);
					});

					compartments.set(
						right.id,
						updateCompartment(
							{
								...right,
								x: right.x + diff,
								width: right.width - diff,
							},
							state.thickness,
						),
					);
				});
			}

			return {
				...state,
				compartments,
				verticals: createVerticals(compartments, state.thickness, state.width),
				horizontals: createHorizontals(compartments, state.thickness, state.material, api),
			};
		});
	};

	const updateCompartmentHeight = (
		compartment: CompartmentType,
		side: HorizontalSide,
		diff: number,
	) => {
		return setStore((state) => {
			const compartments = new Map(state.compartments);
			const minSize = getMinSize(compartment, side, state, api);
			const maxSize = getMaxSize(compartment, side, state, api);

			if (side === "bottom") {
				const newHeight = compartment.height - diff;

				if (newHeight < minSize || newHeight > maxSize) {
					console.warn("Can not resize compartment beyond constraints", {
						newHeight,
						minSize,
						maxSize,
					});

					return { ...state };
				}

				const bottomCompartments = getBottomCompartmentsRecursively(
					compartment,
					compartments,
					state.thickness,
				);

				// biome-ignore lint/complexity/noForEach: Type not set correctly when using for loop
				bottomCompartments.forEach((bottom) => {
					const topCompartments = getTopCompartmentsRecursively(
						bottom,
						compartments,
						state.thickness,
					);

					// biome-ignore lint/complexity/noForEach: Type not set correctly when using for loop
					topCompartments.forEach((top) => {
						compartments.set(
							top.id,
							updateCompartment(
								{
									...top,
									height: top.height - diff,
									y: top.y + diff,
								},
								state.thickness,
							),
						);
					});

					compartments.set(
						bottom.id,
						updateCompartment(
							{
								...bottom,
								height: bottom.height + diff,
							},
							state.thickness,
						),
					);
				});
			}

			if (side === "top") {
				const newHeight = compartment.height + diff;

				if (newHeight < minSize || newHeight > maxSize) {
					console.warn("Can not resize compartment beyond constraints", {
						newHeight,
						minSize,
						maxSize,
					});

					return { ...state };
				}

				const topCompartments = getTopCompartmentsRecursively(
					compartment,
					compartments,
					state.thickness,
				);

				// biome-ignore lint/complexity/noForEach: Type not set correctly when using for loop
				topCompartments.forEach((top) => {
					const bottomCompartments = getBottomCompartmentsRecursively(
						top,
						compartments,
						state.thickness,
					);

					// biome-ignore lint/complexity/noForEach: Type not set correctly when using for loop
					bottomCompartments.forEach((bottom) => {
						compartments.set(
							bottom.id,
							updateCompartment(
								{
									...bottom,
									height: bottom.height + diff,
								},
								state.thickness,
							),
						);
					});

					compartments.set(
						top.id,
						updateCompartment(
							{
								...top,
								y: top.y + diff,
								height: top.height - diff,
							},
							state.thickness,
						),
					);
				});
			}

			return {
				...state,
				compartments,
				verticals: createVerticals(compartments, state.thickness, state.width),
				horizontals: createHorizontals(compartments, state.thickness, state.material, api),
			};
		});
	};

	return (
		<CabinetStoreContext.Provider
			value={{
				api,
				setApi,

				state: store,
				setStore,

				undo,
				redo,
				undoDisabled: !pastStates.length,
				redoDisabled: !futureStates.length,

				getOuterConstraints,
				setThickness,
				setMaterial,

				splitVertically,
				splitHorizontally,

				splitVerticallyRecursivelyOnResize,
				splitHorizontallyRecursivelyOnResize,
				mergeCompartmentsOnResize,

				addShelf,
				removeShelf,

				addDoor,
				removeDoor,
				addDoubleDoor,

				mergeCompartment,

				updateCompartmentWidth,
				updateCompartmentHeight,
			}}
		>
			{children}
		</CabinetStoreContext.Provider>
	);
};
